// Static country dial-code dataset for the phone inputs (ISS-02 / ISS-18).
// Kept static on purpose - this never changes per-deploy, so there is no reason
// to DB-drive it. India (+91) is first and is the default selection. Flags are
// emoji so we ship no image assets. Covers Flora Dine's common export markets
// plus the widely-used dial codes; extend as needed.

export interface Country {
  /** Display name. */
  name: string;
  /** ISO 3166-1 alpha-2 code (used for React keys + flag emoji). */
  iso2: string;
  /** E.164 dial code, including the leading "+". */
  dialCode: string;
  /** Emoji flag. */
  flag: string;
}

export const COUNTRIES: Country[] = [
  { name: 'India', iso2: 'IN', dialCode: '+91', flag: '🇮🇳' },
  { name: 'United States', iso2: 'US', dialCode: '+1', flag: '🇺🇸' },
  { name: 'United Kingdom', iso2: 'GB', dialCode: '+44', flag: '🇬🇧' },
  { name: 'United Arab Emirates', iso2: 'AE', dialCode: '+971', flag: '🇦🇪' },
  { name: 'Australia', iso2: 'AU', dialCode: '+61', flag: '🇦🇺' },
  { name: 'Canada', iso2: 'CA', dialCode: '+1', flag: '🇨🇦' },
  { name: 'Germany', iso2: 'DE', dialCode: '+49', flag: '🇩🇪' },
  { name: 'France', iso2: 'FR', dialCode: '+33', flag: '🇫🇷' },
  { name: 'Netherlands', iso2: 'NL', dialCode: '+31', flag: '🇳🇱' },
  { name: 'Spain', iso2: 'ES', dialCode: '+34', flag: '🇪🇸' },
  { name: 'Italy', iso2: 'IT', dialCode: '+39', flag: '🇮🇹' },
  { name: 'Ireland', iso2: 'IE', dialCode: '+353', flag: '🇮🇪' },
  { name: 'Belgium', iso2: 'BE', dialCode: '+32', flag: '🇧🇪' },
  { name: 'Switzerland', iso2: 'CH', dialCode: '+41', flag: '🇨🇭' },
  { name: 'Sweden', iso2: 'SE', dialCode: '+46', flag: '🇸🇪' },
  { name: 'Norway', iso2: 'NO', dialCode: '+47', flag: '🇳🇴' },
  { name: 'Denmark', iso2: 'DK', dialCode: '+45', flag: '🇩🇰' },
  { name: 'Saudi Arabia', iso2: 'SA', dialCode: '+966', flag: '🇸🇦' },
  { name: 'Qatar', iso2: 'QA', dialCode: '+974', flag: '🇶🇦' },
  { name: 'Kuwait', iso2: 'KW', dialCode: '+965', flag: '🇰🇼' },
  { name: 'Bahrain', iso2: 'BH', dialCode: '+973', flag: '🇧🇭' },
  { name: 'Oman', iso2: 'OM', dialCode: '+968', flag: '🇴🇲' },
  { name: 'Singapore', iso2: 'SG', dialCode: '+65', flag: '🇸🇬' },
  { name: 'Malaysia', iso2: 'MY', dialCode: '+60', flag: '🇲🇾' },
  { name: 'Japan', iso2: 'JP', dialCode: '+81', flag: '🇯🇵' },
  { name: 'China', iso2: 'CN', dialCode: '+86', flag: '🇨🇳' },
  { name: 'Hong Kong', iso2: 'HK', dialCode: '+852', flag: '🇭🇰' },
  { name: 'New Zealand', iso2: 'NZ', dialCode: '+64', flag: '🇳🇿' },
  { name: 'South Africa', iso2: 'ZA', dialCode: '+27', flag: '🇿🇦' },
  { name: 'Nigeria', iso2: 'NG', dialCode: '+234', flag: '🇳🇬' },
  { name: 'Kenya', iso2: 'KE', dialCode: '+254', flag: '🇰🇪' },
  { name: 'Bangladesh', iso2: 'BD', dialCode: '+880', flag: '🇧🇩' },
  { name: 'Sri Lanka', iso2: 'LK', dialCode: '+94', flag: '🇱🇰' },
  { name: 'Nepal', iso2: 'NP', dialCode: '+977', flag: '🇳🇵' },
  { name: 'Brazil', iso2: 'BR', dialCode: '+55', flag: '🇧🇷' },
  { name: 'Mexico', iso2: 'MX', dialCode: '+52', flag: '🇲🇽' },
  { name: 'Portugal', iso2: 'PT', dialCode: '+351', flag: '🇵🇹' },
  { name: 'Poland', iso2: 'PL', dialCode: '+48', flag: '🇵🇱' },
  { name: 'Turkey', iso2: 'TR', dialCode: '+90', flag: '🇹🇷' },
  { name: 'Indonesia', iso2: 'ID', dialCode: '+62', flag: '🇮🇩' },
  { name: 'Thailand', iso2: 'TH', dialCode: '+66', flag: '🇹🇭' },
  { name: 'Vietnam', iso2: 'VN', dialCode: '+84', flag: '🇻🇳' },
  { name: 'Philippines', iso2: 'PH', dialCode: '+63', flag: '🇵🇭' },
  { name: 'South Korea', iso2: 'KR', dialCode: '+82', flag: '🇰🇷' },
];

/** The default country (India). */
export const DEFAULT_COUNTRY: Country = COUNTRIES[0];

/** First country matching a dial code (dial codes are not unique, e.g. +1). */
export function findByDialCode(dialCode: string): Country | undefined {
  return COUNTRIES.find((c) => c.dialCode === dialCode);
}
